import React, { useLayoutEffect, useState } from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Button,
  Col,
  Form,
  InputFormItem,
  LayoutContentWithHeader,
  LayoutContentWrapper,
  Loader,
  PlacesAutocompleteFormItem,
  Row,
  SelectFormItem,
  basicStyle,
} from '@jjj/components';
import { t } from '../../../../i18n';
import { RootState } from '../../../../redux/store';
import { getCurrentCountryContext } from '../../../../helpers/countriesHelpers';
import actions from './actions';
import { ILegalEntityFormPayload, LegalEntityFieldUpdates, LegalEntityFormField } from './types';
import { IGoogleAddressData, IProps } from './createLegalEntity.types';
import { LEGAL_ENTITIES_PATH } from '../paths';
import CreateLegalEntityWrapper from './createLegalEntity.style';

const getRequiredFields = (hasStates: boolean): LegalEntityFormField[] => {
  const base: LegalEntityFormField[] = ['name', 'street', 'city', 'zip'];

  return hasStates ? [...base, 'state_id'] : base;
};

const CreateLegalEntity: React.FC<IProps> = ({
  form,
  states,
  isLoadingStates,
  isLoading,
  isSubmitting,
  resetForm,
  loadStatesRequest,
  loadRequest,
  changeField,
  changeFields,
  touchAll,
  createRequest,
  updateRequest,
}) => {
  const navigate = useNavigate();
  const { id: editingId } = useParams<{ id?: string }>();
  const isEdit = Boolean(editingId);
  const { gutter } = basicStyle;
  // Reducer state survives between mounts. Gate the form render in edit mode
  // until this mount has dispatched its own loadRequest so we never paint the
  // empty-initState form before the fetch fires.
  const [initialized, setInitialized] = useState(false);

  // useLayoutEffect (not useEffect) so the reset dispatch + setInitialized
  // commit BEFORE the browser paints the first render. Prevents stale Redux
  // form state (e.g. validation errors from the previous mount) from flashing.
  useLayoutEffect(() => {
    if (editingId) {
      loadRequest(editingId);
    } else {
      resetForm();
      loadStatesRequest();
    }
    setInitialized(true);
  }, [resetForm, loadStatesRequest, loadRequest, editingId]);

  const handleCancel = () => navigate(LEGAL_ENTITIES_PATH);

  const matchStateId = (googleState?: string) => {
    if (!googleState) return '';
    const match = states.find(s => s.code === googleState || s.name === googleState);

    return match?.id ?? '';
  };

  const handleSelectAddress = (addressData: IGoogleAddressData) => {
    const { street, city, zip, state } = addressData;
    const stateId = matchStateId(state);

    const updates: LegalEntityFieldUpdates = {
      ...(street && { street }),
      ...(city && { city }),
      ...(zip && { zip }),
      ...(stateId && { state_id: stateId }),
    };

    if (Object.keys(updates).length > 0) {
      changeFields(updates);
    }
  };

  const hasStates = states?.length > 0;
  const requiredFields = getRequiredFields(hasStates);
  const validateRequired = () => requiredFields.every(field => Boolean(form[field].value?.trim()));

  const handleSubmit = () => {
    if (!validateRequired()) {
      touchAll();

      return;
    }

    const payload: ILegalEntityFormPayload = {
      name: form.name.value.trim(),
      tax_registration_number: form.tax_registration_number.value.trim(),
      street: form.street.value.trim(),
      city: form.city.value.trim(),
      zip: form.zip.value.trim(),
      ...(hasStates ? { state_id: form.state_id.value } : {}),
    };

    if (isEdit && editingId) {
      updateRequest(editingId, payload);
    } else {
      createRequest(payload);
    }
  };

  const pageTitle = isEdit
    ? t('dash.admin.legalEntities.edit_page_title')
    : t('dash.admin.legalEntities.create_page_title');

  const getInlineError = (field: LegalEntityFormField): string => {
    if (form[field].error) return form[field].error;

    if (form[field].touched && requiredFields.includes(field) && !form[field].value?.trim()) {
      return t('dash.admin.legalEntities.errors.required');
    }

    return '';
  };

  const getStatus = (field: LegalEntityFormField) => (getInlineError(field) ? 'error' : '');

  if (!initialized || (isEdit && isLoading)) {
    return (
      <LayoutContentWrapper>
        <Helmet defer={false}>
          <title>{pageTitle}</title>
        </Helmet>
        <Loader />
      </LayoutContentWrapper>
    );
  }

  return (
    <LayoutContentWrapper>
      <Helmet defer={false}>
        <title>{pageTitle}</title>
      </Helmet>
      <CreateLegalEntityWrapper>
        <LayoutContentWithHeader
          header={pageTitle}
          icon={isEdit ? 'edit' : 'plus-circle'}
          backUrl={LEGAL_ENTITIES_PATH}
        >
          <Row style={basicStyle.rowStyle} gutter={gutter * 2} type="flex">
            <Col xxl={12} lg={18} md={24} sm={24} xs={24} style={basicStyle.colStyle}>
              <h3 className="section-title">{t('dash.admin.legalEntities.section_details')}</h3>
              <Form>
                <Row gutter={gutter * 2}>
                  <Col xs={24}>
                    <InputFormItem
                      labelName={t('dash.admin.legalEntities.form.name_label')}
                      isRequired
                      value={form.name.value}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => changeField('name', event.target.value)}
                      validateStatus={getStatus('name')}
                      help={getInlineError('name')}
                      data-cy="legal-entities-create-name"
                    />
                  </Col>
                </Row>
                <Row gutter={gutter * 2}>
                  <Col xs={24}>
                    <InputFormItem
                      labelName={`${t('dash.admin.legalEntities.form.tax_label')} ${t(
                        'dash.admin.legalEntities.form.optional_suffix',
                      )}`}
                      value={form.tax_registration_number.value}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        changeField('tax_registration_number', event.target.value)
                      }
                      validateStatus={getStatus('tax_registration_number')}
                      help={getInlineError('tax_registration_number')}
                      data-cy="legal-entities-create-tax_registration_number"
                    />
                  </Col>
                </Row>
                <Row gutter={gutter * 2}>
                  <Col xs={24}>
                    <PlacesAutocompleteFormItem
                      labelName={t('dash.admin.legalEntities.form.street_label')}
                      placeholder=""
                      isRequired
                      value={form.street.value}
                      onChange={(_field: string, value: string) => changeField('street', value)}
                      onSelect={handleSelectAddress}
                      validateStatus={getStatus('street')}
                      help={getInlineError('street')}
                      searchOptions={{
                        types: ['address'],
                        componentRestrictions: { country: getCurrentCountryContext().code },
                      }}
                      data-cy="legal-entities-create-street"
                      stickyLabel
                    />
                  </Col>
                </Row>
                <Row gutter={gutter * 2}>
                  <Col xs={24}>
                    <InputFormItem
                      labelName={t('dash.admin.legalEntities.form.city_label')}
                      isRequired
                      value={form.city.value}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => changeField('city', event.target.value)}
                      validateStatus={getStatus('city')}
                      help={getInlineError('city')}
                      data-cy="legal-entities-create-city"
                    />
                  </Col>
                </Row>
                {hasStates && (
                  <Row gutter={gutter * 2}>
                    <Col xs={24}>
                      <SelectFormItem
                        labelName={t('dash.admin.legalEntities.form.state_label')}
                        isRequired
                        value={form.state_id.value || null}
                        onChange={(value: string) => changeField('state_id', value)}
                        optionValues={states}
                        loading={isLoadingStates}
                        validateStatus={getStatus('state_id')}
                        help={getInlineError('state_id')}
                        showSearch
                        data-cy="legal-entities-create-state_id"
                      />
                    </Col>
                  </Row>
                )}
                <Row gutter={gutter * 2}>
                  <Col xs={24}>
                    <InputFormItem
                      labelName={t('dash.admin.legalEntities.form.zip_label')}
                      isRequired
                      value={form.zip.value}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => changeField('zip', event.target.value)}
                      validateStatus={getStatus('zip')}
                      help={getInlineError('zip')}
                      data-cy="legal-entities-create-zip"
                    />
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
        </LayoutContentWithHeader>
        <div className="form-footer">
          <Button
            type="secondary"
            onClick={handleCancel}
            disabled={isSubmitting}
            data-cy="legal-entities-create-cancel"
          >
            {t('dash.common.button.cancel_label')}
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={isSubmitting}
            data-cy="legal-entities-create-save"
          >
            {t('dash.common.button.save_label')}
          </Button>
        </div>
      </CreateLegalEntityWrapper>
    </LayoutContentWrapper>
  );
};

const mapStateToProps = (state: RootState) => ({
  form: state.CreateLegalEntity.form,
  states: state.CreateLegalEntity.states,
  isLoadingStates: state.CreateLegalEntity.isLoadingStates,
  isLoading: state.CreateLegalEntity.isLoading,
  isSubmitting: state.CreateLegalEntity.isSubmitting,
});

const mapDispatchToProps = (dispatch: Dispatch<RootState>) => ({
  resetForm: () => dispatch(actions.resetForm()),
  loadStatesRequest: () => dispatch(actions.loadStatesRequest()),
  loadRequest: (id: string) => dispatch(actions.loadRequest(id)),
  changeField: (field: LegalEntityFormField, value: string) => dispatch(actions.changeField(field, value)),
  changeFields: (updates: LegalEntityFieldUpdates) => dispatch(actions.changeFields(updates)),
  touchAll: () => dispatch(actions.touchAll()),
  createRequest: (data: ILegalEntityFormPayload) => dispatch(actions.createRequest(data)),
  updateRequest: (id: string, data: ILegalEntityFormPayload) => dispatch(actions.updateRequest(id, data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateLegalEntity);
